import {
  Button,
  ColumnLayout,
  Container,
  Form,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
} from '@cloudscape-design/components';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import useSingleGcpCert from '../../../../hooks/query/useSingleGcpCert';
import useReopenGcpCert from '../../../../hooks/mutate/useReopenGcpCert';
import useUpdateGcpCertDetails from '../../../../hooks/mutate/useUpdateGcpCertDetails';
import type { GcpCert } from '../../../../types/gcpCert';

const CERTIFICATE_TYPE_OPTIONS = [
  { label: 'SSL certificates/Classic Certificates', value: 'ssl_classic' },
  { label: 'Self-managed certificates/certificatev2', value: 'self_managed_v2' },
];

const CERTIFICATE_VIEWER_DOMAIN = '@globe.com.ph';

const validateCertificateViewer = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return 'Certificate Viewer (distribution list) is required.';
  const items = trimmed.split(',').map((s) => s.trim()).filter(Boolean);
  if (items.length === 0) return 'Certificate Viewer (distribution list) is required.';
  const invalid = items.filter((item) => !item.toLowerCase().endsWith(CERTIFICATE_VIEWER_DOMAIN));
  if (invalid.length > 0) {
    return `Each value must be a distribution list (DL) ending with ${CERTIFICATE_VIEWER_DOMAIN}. Invalid: ${invalid.join(', ')}`;
  }
  return null;
};

const ReopenGcpCertPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: cert, isLoading } = useSingleGcpCert(id!);
  const { mutateAsync: reopenCert, isLoading: isReopening } = useReopenGcpCert(id!);
  const { mutateAsync: updateCertDetails, isLoading: isUpdating } = useUpdateGcpCertDetails(id!);

  const d = (cert?.details ?? {}) as Record<string, unknown>;
  const [certificateType, setCertificateType] = useState(String(d.certificateType ?? ''));
  const [subnetworkName, setSubnetworkName] = useState(String(d.subnetworkName ?? ''));
  const [networkProjectId, setNetworkProjectId] = useState(String(d.networkProjectId ?? ''));
  const [certificateViewer, setCertificateViewer] = useState(String(d.certificateViewer ?? ''));
  const [subDomains, setSubDomains] = useState<Array<{ id: string; value: string }>>(() =>
    ((d.dnsPrefixes as string[] | undefined) ?? []).map((value: string, i: number) => ({
      id: `sd-${Date.now()}-${i}`,
      value: String(value).trim(),
    })).filter((s) => s.value)
  );
  const [subDomainInput, setSubDomainInput] = useState('');
  const [subDomainError, setSubDomainError] = useState('');

  useEffect(() => {
    if (!cert) return;
    const details = (cert.details ?? {}) as Record<string, unknown>;
    setCertificateType(String(details.certificateType ?? ''));
    setSubnetworkName(String(details.subnetworkName ?? ''));
    setNetworkProjectId(String(details.networkProjectId ?? ''));
    setCertificateViewer(String(details.certificateViewer ?? ''));
    setSubDomains(
      ((details.dnsPrefixes as string[] | undefined) ?? [])
        .map((value: string, i: number) => ({ id: `sd-${Date.now()}-${i}`, value: String(value).trim() }))
        .filter((s) => s.value)
    );
  }, [cert?.gcpCertID, cert?.lastUpdatedTime]);

  // Redirect to detail if cert is not REJECTED (do not call navigate during render)
  useEffect(() => {
    if (!cert || isLoading || !id) return;
    if (cert.status !== 'REJECTED') {
      navigate(`/gcpCerts/${id}`, { replace: true });
    }
  }, [cert, isLoading, id, navigate]);

  if (isLoading || !id) return <LoadingSpinner />;
  if (!cert) return <LoadingSpinner />;
  if (cert.status !== 'REJECTED') return null;

  const handleAddSubDomain = () => {
    const v = subDomainInput.trim();
    if (!v) {
      setSubDomainError('');
      return;
    }
    const normalized = v.toLowerCase();
    if (subDomains.some((s) => s.value.toLowerCase() === normalized)) {
      setSubDomainError('This sub-domain has already been added.');
      return;
    }
    setSubDomainError('');
    setSubDomains((prev) => [...prev, { id: `sd-${Date.now()}`, value: v }]);
    setSubDomainInput('');
  };

  const handleRemoveSubDomain = (id: string) => {
    setSubDomains((prev) => prev.filter((s) => s.id !== id));
  };

  const handleSubmit = () => {
    const certViewerError = validateCertificateViewer(certificateViewer);
    if (certViewerError) return;
    const seen = new Set<string>();
    const dnsPrefixes = subDomains
      .map((s) => s.value.trim())
      .filter((v) => {
        if (!v) return false;
        const key = v.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    if (dnsPrefixes.length === 0) {
      setSubDomainError('At least one sub-domain is required.');
      return;
    }
    setSubDomainError('');
    const existingDetails = cert.details || {};
    const details: Record<string, unknown> = {
      ...existingDetails,
      certificateType: certificateType || undefined,
      subnetworkName: subnetworkName.trim() || undefined,
      networkProjectId: networkProjectId.trim() || undefined,
      certificateViewer: certificateViewer.trim(),
      dnsPrefixes,
    };

    (async () => {
      try {
        await reopenCert();
        await updateCertDetails(details);
        navigate(`/gcpCerts/${id}`);
      } catch {
        // Error handled by mutation
      }
    })();
  };

  const handleCancel = () => {
    navigate(`/gcpCerts/${id}`);
  };

  const applicationName = cert.details?.applicationName || cert.details?.application || cert.details?.applicationAlias || '';
  const applicationDns = cert.details?.dnsDomainName ?? '';
  const platformName =
    typeof cert.details?.platformName === 'object' && cert.details?.platformName !== null && 'label' in (cert.details.platformName as object)
      ? (cert.details.platformName as { label: string }).label
      : String(cert.details?.platformName ?? '');
  const environment = cert.details?.environment ?? '';
  const projectId = cert.details?.projectId ?? (cert as GcpCert & { projectId?: string }).projectId ?? '';
  const applicationOwner = cert.details?.applicationOwner ?? 'N/A';
  const applicationOwnerEmail = cert.details?.applicationOwnerEmail ?? 'N/A';
  const wbsCode = (cert.details?.wbsCode as string)?.trim() || 'N/A';
  const costCenter = (cert.details?.costCenter as string)?.trim() || 'N/A';

  return (
    <Form
      header={
        <Header
          variant="h1"
        >
          Reopen GCP Certificate Request
        </Header>
      }
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="primary" onClick={handleSubmit} loading={isReopening || isUpdating}>
            Submit
          </Button>
          <Button variant="link" onClick={handleCancel}>
            Cancel
          </Button>
        </SpaceBetween>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        <Container header={<Header variant="h2">Application Certificate</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <SpaceBetween direction="vertical" size="m">
              <FormField label="Application name">
                <Input value={applicationName} readOnly disabled />
              </FormField>
              <FormField label="Application DNS">
                <Input value={applicationDns} readOnly disabled />
              </FormField>
              <FormField
                label="SUB_DOMAINS"
                description="Add one or more sub-domains."
                errorText={subDomainError || undefined}
              >
                <SpaceBetween direction="vertical" size="xs">
                  <SpaceBetween direction="horizontal" size="xs">
                    <Input
                      value={subDomainInput}
                      onChange={({ detail }) => {
                        setSubDomainInput(detail.value);
                        if (subDomainError) setSubDomainError('');
                      }}
                      placeholder="Sub-domain"
                      invalid={!!subDomainError}
                      onKeyDown={(e) => e.detail.key === 'Enter' && handleAddSubDomain()}
                    />
                    <Button onClick={handleAddSubDomain}>Add sub-domain</Button>
                  </SpaceBetween>
                  {subDomains.length > 0 && (
                    <SpaceBetween direction="vertical" size="xs">
                      {subDomains.map((sd) => (
                        <div
                          key={sd.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '6px 8px',
                            border: '1px solid #ddd',
                            borderRadius: 4,
                            background: 'var(--color-background-container)',
                          }}
                        >
                          <span style={{ flex: 1 }}>{sd.value}</span>
                          <Button
                            variant="inline-icon"
                            iconName="remove"
                            ariaLabel="Remove"
                            onClick={() => handleRemoveSubDomain(sd.id)}
                          />
                        </div>
                      ))}
                    </SpaceBetween>
                  )}
                </SpaceBetween>
              </FormField>
            </SpaceBetween>
            <SpaceBetween direction="vertical" size="m">
              <FormField label="Certificate Type">
                <Select
                  selectedOption={
                    certificateType
                      ? {
                          label: CERTIFICATE_TYPE_OPTIONS.find((o) => o.value === certificateType)?.label ?? certificateType,
                          value: certificateType,
                        }
                      : null
                  }
                  onChange={({ detail }) => setCertificateType(detail.selectedOption?.value ?? '')}
                  options={CERTIFICATE_TYPE_OPTIONS}
                  placeholder="Select Certificate Type"
                />
              </FormField>
              <FormField label="Cluster repo name (Platform name)">
                <Input value={platformName} readOnly disabled />
              </FormField>
              <FormField label="Subnetwork name">
                <Input
                  value={subnetworkName}
                  onChange={({ detail }) => setSubnetworkName(detail.value)}
                  placeholder="Enter subnetwork name"
                />
              </FormField>
              <FormField label="Network project ID">
                <Input
                  value={networkProjectId}
                  onChange={({ detail }) => setNetworkProjectId(detail.value)}
                  placeholder="Enter network project ID"
                />
              </FormField>
              <FormField
                label="Certificate Viewer"
                description={`Distribution list (DL) as comma-separated values. Each value must end with ${CERTIFICATE_VIEWER_DOMAIN}`}
                errorText={validateCertificateViewer(certificateViewer) ?? undefined}
                constraintText="Required"
              >
                <Input
                  value={certificateViewer}
                  onChange={({ detail }) => setCertificateViewer(detail.value)}
                  invalid={!!validateCertificateViewer(certificateViewer)}
                />
              </FormField>
            </SpaceBetween>
          </ColumnLayout>
        </Container>

        <Container header={<Header variant="h2">Tag Details</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <FormField label="Application Owner">
              <Input value={applicationOwner} readOnly disabled />
            </FormField>
            <FormField label="Application Owner Mail">
              <Input value={applicationOwnerEmail} readOnly disabled />
            </FormField>
            <FormField label="WBS_CODE">
              <Input value={wbsCode} readOnly disabled />
            </FormField>
            <FormField label="Application Name">
              <Input value={applicationName} readOnly disabled />
            </FormField>
            <FormField label="GCP_PROJECT_ID">
              <Input value={projectId} readOnly disabled />
            </FormField>
            <FormField label="COST_Center">
              <Input value={costCenter} readOnly disabled />
            </FormField>
            <FormField label="ENVIRONMENT">
              <Input value={environment} readOnly disabled />
            </FormField>
          </ColumnLayout>
        </Container>
      </SpaceBetween>
    </Form>
  );
};

export default ReopenGcpCertPage;
